// Microsoft Clarity user tagging helper.
// The base Clarity script is loaded in public/index.html (project id x9fmm6vap9).
// These helpers identify the logged-in user by email so sessions/recordings can
// be tracked and filtered per user in the Clarity dashboard.

type ClarityFn = (...args: any[]) => void;

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

/**
 * Build a human-friendly display name from an email.
 * e.g. "akshit@gna.energy" -> "Akshit(GNA)", "john.doe@acme.com" -> "John Doe(ACME)"
 */
export const friendlyNameFromEmail = (email: string): string => {
  const [localPart = "", domainPart = ""] = email.split("@");
  // Title-case the local part, treating . _ - as word separators.
  const name = localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  // Organisation = the label before the first dot of the domain, upper-cased.
  const org = domainPart.split(".")[0]?.toUpperCase() ?? "";
  return org ? `${name}(${org})` : name;
};

/**
 * Tag the current Clarity session with the user's email.
 * - "identify" links sessions to a stable user id (raw email) and a friendly name.
 * - "set" adds a custom tag so you can filter/segment dashboards by email.
 */
export const identifyClarityUser = (email?: string | null) => {
  if (!email || typeof window === "undefined" || typeof window.clarity !== "function") {
    return;
  }
  try {
    // custom-id = raw email (stable id), friendly-name = "Akshit(GNA)" (shown in UI)
    window.clarity("identify", email, undefined, undefined, friendlyNameFromEmail(email));
    // Custom tag for easy filtering/segmentation in dashboards and funnels.
    window.clarity("set", "email", email);
  } catch (e) {
    // Analytics must never break the app.
    console.warn("Clarity identify failed", e);
  }
};

/**
 * Re-identify an already-logged-in user from localStorage (e.g. on app boot,
 * where the session is restored without going through the login flow).
 */
export const identifyClarityFromStorage = () => {
  try {
    if (localStorage.getItem("loggedIn") === "true") {
      identifyClarityUser(localStorage.getItem("email"));
    }
  } catch {
    // ignore storage access errors
  }
};
